import uuid from "react-uuid";
import { hexToRgba, isCssVar, resolveCssVar, rgbaToHex } from "./rgba";
import type { GradientStop, LinearGradient } from "./types";

// We use react-uuid (already a direct project dependency, see
// src/shared/hooks/use-upload-media.tsx) rather than nanoid to match project
// conventions and avoid relying on transitively-pulled packages.
/** Generate a fresh unique id for a gradient stop. */
export const makeStopId = (): string => uuid();

/** Black → White linear gradient at 90°. Each call returns fresh stop IDs. */
export const defaultGradient = (): LinearGradient => {
	const stops: GradientStop[] = [
		{ id: makeStopId(), color: "#000000", opacity: 1, position: 0 },
		{ id: makeStopId(), color: "#FFFFFF", opacity: 1, position: 100 },
	];
	return { type: "linear", angle: 90, stops };
};

/**
 * Darken a hex color by a given factor (0–1). Factor 0.1 = 10% darker.
 * Clamps each channel to 0.
 */
const darkenHex = (hex: string, factor: number): string => {
	const { r, g, b } = hexToRgba(hex);
	const dr = Math.round(r * (1 - factor));
	const dg = Math.round(g * (1 - factor));
	const db = Math.round(b * (1 - factor));
	return rgbaToHex({ r: dr, g: dg, b: db, a: 1 }, false).toUpperCase();
};

/**
 * Create a gradient seeded from a solid color.
 * Start stop = the original color, end stop = 10% darker variant.
 * CSS variables are resolved to hex for the darkened stop.
 */
export const gradientFromSolid = (color: string): LinearGradient => {
	const resolvedColor = isCssVar(color) ? resolveCssVar(color) : color;
	const startColor = resolvedColor || "#000000";
	const endColor = darkenHex(startColor, 0.1);
	const stops: GradientStop[] = [
		{ id: makeStopId(), color: startColor, opacity: 1, position: 0 },
		{ id: makeStopId(), color: endColor, opacity: 1, position: 100 },
	];
	return { type: "linear", angle: 90, stops };
};
